// app/src/People.jsx — workplace members + invite-people. Phase 1.
//
// Lists active members (GET /workplaces/{wid}/members) and lets a manager/owner
// generate a shareable invite link (POST /workplaces/{wid}/invitations). The link
// is multi-use until it expires — share it with the whole crew.

function People() {
  const wid = window.crew && window.crew.state.activeWorkplaceId;
  const me = (window.crew && window.crew.state.me) || { workplaces: [] };
  const myWp = (me.workplaces || []).find((w) => w.workplaceId === wid) || {};
  const canInvite = myWp.role === 'owner' || myWp.role === 'manager';

  const [members, setMembers] = React.useState(null);
  const [invite, setInvite] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await window.crew.api(`/workplaces/${wid}/members`, { workplaceId: wid });
        if (!cancelled) setMembers((res && res.members) || []);
      } catch (e) {
        if (!cancelled) { setMembers([]); setErr((e && e.message) || t('people.loadError', 'Could not load members')); }
      }
    })();
    return () => { cancelled = true; };
  }, [wid]);

  const makeInvite = async () => {
    if (busy) return;
    setBusy(true); setErr(null); setCopied(false);
    try {
      const res = await window.crew.api(`/workplaces/${wid}/invitations`, { method: 'POST', workplaceId: wid, body: { role: 'crew' } });
      setInvite(res);
    } catch (e) {
      setErr((e && e.message) || t('people.inviteError', 'Could not create invite'));
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!invite) return;
    try { await navigator.clipboard.writeText(invite.url); setCopied(true); } catch { /* ignore */ }
  };

  const card = { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 12 };

  return (
    <KShell tab="you">
      <KHeader back title={t('people.title', 'People')} />
      <div style={{ padding: '12px 16px' }}>
        {canInvite && (
          <section style={card}>
            <div style={{ fontWeight: 700, color: 'var(--ink)', marginBottom: 4 }}>{t('people.inviteCrew', 'Invite your crew')}</div>
            <div style={{ fontSize: 13, color: 'var(--sub)', marginBottom: 12 }}>
              {t('people.inviteDesc', 'Share one link — anyone who opens it joins this workplace.')}
            </div>
            {!invite ? (
              <button onClick={makeInvite} disabled={busy} style={btn(busy)}>
                {busy ? t('common.creating', 'Creating…') : t('people.createLink', 'Create invite link')}
              </button>
            ) : (
              <div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input readOnly value={invite.url} style={{
                    flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--line)', background: 'var(--surface-2)', color: 'var(--ink)', fontSize: 12,
                  }} />
                  <button onClick={copy} style={{ ...btn(false), width: 'auto', padding: '10px 14px' }}>
                    {copied ? '✓' : t('common.copy', 'Copy')}
                  </button>
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--sub)', marginTop: 8 }}>
                  {t('people.validNote', 'Valid for 14 days · joins as crew.')} <button onClick={makeInvite} style={linkBtn}>{t('people.newLink', 'New link')}</button>
                </div>
              </div>
            )}
          </section>
        )}

        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--on-soft)', margin: '6px 4px 8px' }}>
          {t('people.members', 'MEMBERS')}{members ? ` · ${members.length}` : ''}
        </div>
        {members == null && <div style={{ padding: 16, color: 'var(--on-dim)' }}>…</div>}
        {(members || []).map((m) => (
          <div key={m.userId} style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: 12 }}>
            <Avatar name={m.displayName} url={m.avatarUrl} size={32} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>{m.displayName || t('common.member', 'Member')}</div>
              <div style={{ fontSize: 11.5, color: 'var(--sub)' }}>{m.preferredLang ? window.langName(m.preferredLang) : ''}</div>
            </div>
            <span style={{
              fontSize: 9, fontWeight: 800, letterSpacing: '.4px', padding: '3px 8px', borderRadius: 999,
              background: m.role === 'owner' || m.role === 'manager' ? 'var(--primary-soft)' : 'var(--surface-2)',
              color: m.role === 'owner' || m.role === 'manager' ? 'var(--primary)' : 'var(--sub)',
            }}>{t('role.' + (m.role || 'crew'), m.role || 'crew').toUpperCase()}</span>
          </div>
        ))}

        {err && <div style={{ color: 'var(--primary)', fontSize: 13, marginTop: 10 }}>{err}</div>}
      </div>
    </KShell>
  );

  function btn(b) {
    return {
      width: '100%', padding: '12px 16px', cursor: b ? 'default' : 'pointer',
      borderRadius: 'var(--radius-sm)', border: 'none',
      background: 'var(--primary)', color: 'var(--primary-ink)', fontWeight: 700, fontSize: 14, opacity: b ? 0.6 : 1,
    };
  }
}

const linkBtn = { border: 'none', background: 'none', color: 'var(--primary)', fontWeight: 700, cursor: 'pointer', fontSize: 11.5 };

Object.assign(window, { People });
